import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import cn from 'classnames';

import { CheckoutOrderFragment } from '@websky/graphql';
import Field from './Field/Field';
import { useTheme } from '../../../../../theme';
import InfoLinksLabel from '../../../../../InfoLinksLabel';
import { BaseButton } from '../../../../../BaseComponents';
import { validatePhone } from '@shared/lib/validate';
import { ExchangeRefundCustomer, ExchangeRefundFormStateProps } from '../../model';

interface Props {
	className?: string;
	type: 'checkout' | 'exchange' | 'refund';
	title?: string;
	description?: string;
	initialValues?: ExchangeRefundCustomer;
	buttonText?: string;
	cancelButtonText?: string;
	isLoading?: boolean;
	requiredComment?: boolean;
	order?: CheckoutOrderFragment;
	onSubmit: (data: ExchangeRefundCustomer) => void;
	onCancel?: () => void;
}

const ExchangeRefundCreateForm: React.FC<Props> = ({
	className,
	type,
	title,
	description,
	initialValues,
	buttonText,
	cancelButtonText,
	isLoading,
	requiredComment,
	order,
	onSubmit,
	onCancel
}) => {
	const { ExchangeRefundCreateForm: theme } = useTheme('Entities').Order;
	const { t } = useTranslation('CreateRequest');

	const formValues = { ...initialValues };

	formValues.phone = validatePhone(formValues.phone);

	const handleOnSubmit = (data: ExchangeRefundFormStateProps) => {
		onSubmit({
			name: data.name || '',
			email: data.email || '',
			phone: data.phone || '',
			comment: data.comment || ''
		});
	};

	return (
		<Form<ExchangeRefundFormStateProps> onSubmit={handleOnSubmit} initialValues={formValues}>
			{({ handleSubmit, valid }) => (
				<form className={cn(theme.form, { [theme.form_withCancel]: onCancel })} onSubmit={handleSubmit}>
					<div className={cn(theme.fields, className)}>
						<div className={theme.header}>
							{<div className={theme.title}>{title ?? t('Your contacts')}</div>}
							{!!description && <div className={theme.description}>{description}</div>}
						</div>

						<div className={theme.contacts}>
							<Field name="name" type="text" label={t('Your name')} className={theme.field} />

							<Field name="email" type="email" label={t('Your email')} className={theme.field} />

							<Field
								name="phone"
								type="phone"
								label={t('Your phone')}
								className={theme.field}
								displayInitialValueAsLocalNumber={false}
							/>
						</div>

						<Field
							name="comment"
							type="comment"
							label={t('Your comments')}
							className={cn(theme.field, theme.field__comment)}
							required={requiredComment}
						/>
					</div>

					<div className={theme.controls}>
						<Field
							name="acceptTerm"
							type="checkbox"
							label={<InfoLinksLabel className={theme.label} order={order} type={type} />}
						/>

						{onCancel && (
							<BaseButton variant="smallSecondary" onClick={onCancel} className={theme.cancel}>
								{cancelButtonText ?? t('Back')}
							</BaseButton>
						)}

						<BaseButton
							variant="action"
							type="submit"
							className={theme.submit}
							isLoading={isLoading}
							disabled={!valid}
							data-next-button={true}
						>
							{buttonText ?? t('Send request')}
						</BaseButton>
					</div>
				</form>
			)}
		</Form>
	);
};

export default ExchangeRefundCreateForm;
